"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { ProductVariant } from "@/hooks/useFunnelState";
import ProductVariantTable from "./price-customization/ProductVariantTable";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import productData from "@/data/productData.json"; // Import productData
import { ProductDataSource, ProductPlanDetails } from "@/types/product"; // Import ProductDataSource and ProductPlanDetails

const typedProductData: ProductDataSource = productData as ProductDataSource; // Type assertion

interface ProductVariantSelectionProps {
  petType: "cat" | "dog";
  petName: string;
  breed: string;
  age: number;
  initialBundlePrices: { S: number; M: number; L: number } | null;
  onSelectVariantAndNavigate: (product: ProductVariant) => void;
  onBack: () => void;
  selectedProduct: ProductVariant | null;
  setSelectedProduct: (product: ProductVariant) => void;
  isLoadingPrice: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  isFetchingBundlePrices: boolean;
}

const ProductVariantSelection: React.FC<ProductVariantSelectionProps> = ({
  petType,
  petName,
  breed,
  age,
  initialBundlePrices,
  onSelectVariantAndNavigate,
  onBack,
  selectedProduct,
  setSelectedProduct,
  isLoadingPrice,
  paymentInterval,
  isFetchingBundlePrices,
}) => {
  const { t } = useTranslation();
  const { showErrorDialog } = useErrorDialog();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productVariants: ProductVariant[] = React.useMemo(() => [
    {
      id: "s",
      name: t("priceCustomization.planNames.basic"),
      description: t("priceCustomization.planNames.basicSubtitle"),
      priceFactor: 0, // Not directly used here, but kept for consistency
      details: typedProductData.v1.s, // Correctly assign details for 's'
    },
    {
      id: "m",
      name: t("priceCustomization.planNames.standard"),
      description: t("priceCustomization.planNames.standardSubtitle"),
      priceFactor: 0,
      details: typedProductData.v1.m, // Correctly assign details for 'm'
    },
    {
      id: "l",
      name: t("priceCustomization.planNames.premium"),
      description: t("priceCustomization.planNames.premiumSubtitle"),
      priceFactor: 0,
      details: typedProductData.v1.l, // Correctly assign details for 'l'
    },
  ], [t]);

  // Define coverage sections for the table
  const coverageSections = React.useMemo(() => [
    {
      type: "data",
      labelKey: "priceCustomization.table.basicCoverageAccidentIllness",
      tooltipKey: "priceCustomization.table.maxYearlyVetCostsTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.sum_insured > 0,
        detailContent: t("priceCustomization.table.maxYearlyVetCostsDetail", { amount: details.disease.sum_insured.toLocaleString('de-CH') }),
      })),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.dentalTreatments",
      tooltipKey: "priceCustomization.table.dentalTreatmentsTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => {
        const accidentCovered = details.disease.dental_treatment_accident > 0;
        const diseaseCovered = details.disease.dental_treatment_disease > 0;
        let detailContent: React.ReactNode;

        if (accidentCovered && diseaseCovered) {
          detailContent = t("priceCustomization.table.dentalAccidentDisease");
        } else if (accidentCovered) {
          detailContent = t("priceCustomization.table.dentalAccident");
        } else {
          detailContent = t("common.no");
        }

        return {
          display: null,
          detailContent: detailContent,
          isCovered: accidentCovered || diseaseCovered,
        };
      }),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.emergencyCare",
      tooltipKey: "priceCustomization.table.emergencyCareTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.emergency_care > 0,
        detailContent: t("priceCustomization.table.emergencyCareDetail", { amount: details.disease.emergency_care.toLocaleString('de-CH') }),
      })),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.behaviourTreatment",
      tooltipKey: "priceCustomization.table.behaviourTreatmentTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.behaviour_treatment > 0,
        detailContent: details.disease.behaviour_treatment > 0 ? t("common.yes") : t("common.no"),
      })),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.complementaryTreatments",
      tooltipKey: "priceCustomization.table.complementaryTreatmentsTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.alternative_treatment > 0,
        detailContent: `CHF ${details.disease.alternative_treatment.toLocaleString('de-CH')}`,
      })),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.preventiveTreatments",
      tooltipKey: "priceCustomization.table.preventiveTreatmentsTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.preventive_treatment > 0,
        detailContent: details.disease.preventive_treatment > 0
          ? `CHF ${details.disease.preventive_treatment.toLocaleString('de-CH')}`
          : t("common.no"),
      })),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.medicalFood",
      tooltipKey: "priceCustomization.table.medicalFoodTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.medical_food > 0,
        detailContent: details.disease.medical_food > 0
          ? `${details.disease.medical_food}% (max. CHF ${details.disease.medical_food_cap.toLocaleString('de-CH')})`
          : t("common.no"),
      })),
    },
    {
      type: "data",
      labelKey: "priceCustomization.table.advancedTreatments",
      tooltipKey: "priceCustomization.table.advancedTreatmentsTooltip",
      values: productVariants.map(variant => (details: ProductPlanDetails, t: (key: string, options?: any) => string) => ({
        display: null,
        isCovered: details.disease.advanced_treatment > 0,
        detailContent: details.disease.advanced_treatment > 0 ? t("common.yes") : t("common.no"),
      })),
    },
  ], [productVariants, t]);

  const handleSubmit = () => {
    if (!selectedProduct) {
      showErrorDialog(t("productVariantSelection.validation.selectProductVariant"));
      return;
    }
    onSelectVariantAndNavigate(selectedProduct);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto mb-24 border-none shadow-none">
      <CardHeader className="text-center">
        <CardTitle className="text-3xl font-bold">
          {t("banner-header-2", { petName: petName?.trim() || t("common.yourPet") })}
        </CardTitle>
        <CardDescription>
          {t("productVariantSelection.description", { petName: petName?.trim() || t("common.yourPet") })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 md:space-y-8 pt-2 md:pt-0">
        <ProductVariantTable
          productVariants={productVariants}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          isLoadingPrice={isLoadingPrice}
          coverageSections={coverageSections}
          initialBundlePrices={initialBundlePrices}
          paymentInterval={paymentInterval}
          isFetchingBundlePrices={isFetchingBundlePrices}
        />
      </CardContent>
      <CardFooter className="flex justify-between gap-4">
        <Button variant="outline" onClick={onBack} className="w-full" disabled={isLoadingPrice}>
          {t("productVariantSelection.back")}
        </Button>
        <Button onClick={handleSubmit} className="w-full bg-continue-button text-continue-button-foreground hover:bg-continue-button/90" disabled={isLoadingPrice || !selectedProduct}>
          {isLoadingPrice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("productVariantSelection.next")}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProductVariantSelection;